/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { CustomTextProjectKind, CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';

export type CustomTextProjectsTableProps = TableProps<CustomTextProjectStore> & {
    areClassificationProjects: boolean;
    onProjectClick: (appId: string, projectKind: CustomTextProjectKind) => void;
};

const messages = defineMessages({
    name: {
        id: 'customText.customTextProjectsTable.name',
        defaultMessage: 'Name'
    },
    lastModified: {
        id: 'customText.customTextProjectsTable.lastModified',
        defaultMessage: 'Last modified'
    },
    language: {
        id: 'customText.customTextProjectsTable.language',
        defaultMessage: 'Language'
    },
    status: {
        id: 'customText.customTextProjectsTable.status',
        defaultMessage: 'Status'
    },
    type: {
        id: 'customText.customTextProjectsTable.type',
        defaultMessage: 'Type'
    },
    singleClassification: {
        id: 'customText.customTextProjectsTable.singleClassification',
        defaultMessage: 'Single label classification'
    },
    multiClassification: {
        id: 'customText.customTextProjectsTable.multiClassification',
        defaultMessage: 'Multi label classification'
    }
});

const customTextProjectsTableTheme = getScopedTheme('CustomTextProjectsTable');
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: customTextProjectsTableTheme.projectTypeIconColor, fontSize: '16px' }
});
const nameLinkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(100% - 24px)'
    }
});

/**
 * Displays a table of all extraction projects.
 */
export const CustomTextProjectsTable = observer((props: CustomTextProjectsTableProps) => {
    const { selection, items, onProjectClick, sortKey, areClassificationProjects, ...rest } = props;
    const { localize, locale } = useLocalization();
    const {
        rootStore: { customTextCultureStorehouse }
    } = useGlobalState();

    const columns: IColumn[] = [
        {
            key: 'name',
            name: localize(messages.name),
            fieldName: 'name',
            minWidth: 300,
            maxWidth: 500,
            isRowHeader: true,
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: CustomTextProjectStore) => (
                <Stack styles={{ root: { maxWidth: '100%' } }} verticalAlign="center" verticalFill horizontal tokens={{ childrenGap: 8 }}>
                    <Icon iconName="Page" styles={iconStyles} />
                    <NameCellLink
                        data-automation-id="projectCell"
                        styles={nameLinkStyles}
                        onClick={() => onProjectClick(item.id, item.projectKind)}>
                        {item.name}
                    </NameCellLink>
                </Stack>
            )
        },
        {
            key: 'modifiedDate',
            name: localize(messages.lastModified),
            fieldName: 'modifiedDate',
            minWidth: 300,
            maxWidth: 300,
            isResizable: true,
            isSorted: sortKey?.key === 'modifiedDate',
            isSortedDescending: sortKey?.key === 'modifiedDate' && sortKey?.order === 'desc',
            onRender: (item: CustomTextProjectStore) => (
                <LuisText variant="medium">{item.modifiedDate?.toLocaleDateString(locale) ?? '-'}</LuisText>
            )
        },
        {
            key: 'culture',
            name: localize(messages.language),
            fieldName: 'culture',
            minWidth: 300,
            maxWidth: 300,
            isResizable: true,
            isSorted: sortKey?.key === 'culture',
            isSortedDescending: sortKey?.key === 'culture' && sortKey?.order === 'desc',
            onRender: (item: CustomTextProjectStore) => customTextCultureStorehouse.findById(item.culture)?.name
        }
    ].concat(
        areClassificationProjects
            ? [
                  {
                      minWidth: 300,
                      maxWidth: 300,
                      isResizable: true,
                      key: 'projectKind',
                      fieldName: 'projectKind',
                      name: localize(messages.type),
                      isSorted: sortKey?.key === 'projectKind',
                      isSortedDescending: sortKey?.key === 'projectKind' && sortKey?.order === 'desc',
                      onRender: (item: CustomTextProjectStore) =>
                          localize(
                              item.projectKind === 'CustomSingleLabelClassification'
                                  ? messages.singleClassification
                                  : messages.multiClassification
                          )
                  }
              ]
            : []
    );

    return <Table<CustomTextProjectStore> items={items} columns={columns} selection={selection} {...rest} />;
});
